import {
  ApiException,
  FileResponse,
  OrderInputModel,
  OrderInternalAuditViewModel,
  OrderInternalDocumentViewModel,
  OrderInternalInputModel,
  OrderInternalViewModel,
  OrdersApi,
  OrdersFormAnonymousViewModel,
  OrdersFormViewModel,
  OrderSuppliersAuditViewModel,
  OrderSuppliersDocumentViewModel,
  OrderSuppliersInputModel,
  OrderSuppliersViewModel,
  OrderType,
  OrderViewModel,
  SupplierUploadInputModel,
  SupplierUploadViewModel,
  WorkflowConfigViewModel
} from '@ail/orders-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/UserManager'
import { Environment } from '../../environment'
import { getBaseQuery } from './apiQuery'

const client = new OrdersApi({
  baseUrl: Environment.OrdersApiUrl,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery: getBaseQuery<ApiException>(),
  endpoints: (builder) => ({
    getOrderSuppliers: builder.query<OrderSuppliersViewModel, { id: string; document?: string }>({
      query: (model) => client.getOrderSuppliers(model.id, model.document),
      providesTags: ['OrdersSuppliers'],
    }),
    getOrderSuppliersDocument: builder.query<OrderSuppliersDocumentViewModel, { id: string; documentId: string }>({
      query: ({ id, documentId }) => client.getOrderSuppliersDocument(id, documentId),
    }),
    getOrdersSuppliers: builder.query<OrderSuppliersViewModel[], { tenantId?: string, orderTypes?: OrderType[] }>({
      query: (model) => client.getOrdersSuppliers(model.tenantId, model.orderTypes),
      providesTags: ['OrdersSuppliers'],
    }),
    getOrderSuppliersAudit: builder.query<OrderSuppliersAuditViewModel[], string>({
      query: (id) => client.getOrderSuppliersAudit(id),
      providesTags: ['OrdersSuppliersAudit'],
    }),
    updateOrderSuppliers: builder.mutation<OrderSuppliersViewModel, { id: string; model: OrderSuppliersInputModel }>({
      query: ({ id, model }) => client.putOrderSuppliers(id, model),
      invalidatesTags: ['OrdersInternal', 'OrdersSuppliers', 'OrdersInternalAudit', 'OrdersSuppliersAudit'],
    }),
    getOrderInternal: builder.query<OrderInternalViewModel, { id: string; document?: string }>({
      query: (model) => client.getOrderInternal(model.id, model.document),
      providesTags: ['OrdersInternal'],
    }),
    getOrderInternalDocument: builder.query<OrderInternalDocumentViewModel, { id: string; documentId: string }>({
      query: ({ id, documentId }) => client.getOrderInternalDocument(id, documentId),
    }),
    getOrdersInternal: builder.query<OrderInternalViewModel[], { orderTypes?: OrderType[] }>({
      query: (model) => client.getOrdersInternal(model.orderTypes),
      providesTags: ['OrdersInternal'],
    }),
    getOrderInternalAudit: builder.query<OrderInternalAuditViewModel[], string>({
      query: (id) => client.getOrderInternalAudit(id),
      providesTags: ['OrdersInternalAudit'],
    }),
    updateOrderInternal: builder.mutation<OrderInternalViewModel, { id: string; model: OrderInternalInputModel }>({
      query: ({ id, model }) => client.putOrderInternal(id, model),
      invalidatesTags: ['OrdersInternal', 'OrdersSuppliers', 'OrdersInternalAudit', 'OrdersSuppliersAudit'],
    }),
    getOrder: builder.query<OrderViewModel, string>({
      query: (orderId) => client.getOrder(orderId),
    }),
    postWorkItem: builder.mutation<OrderViewModel, OrderInputModel>({
      query: (model) => client.postWorkItem('', model),
    }),
    getOrdersForm: builder.query<OrdersFormViewModel, string>({
      query: (formId) => client.getOrdersForm(formId),
    }),
    getOrdersForms: builder.query<OrdersFormViewModel[], void>({
      query: () => client.getOrdersForms(),
    }),
    getWorkItems: builder.query<OrderViewModel[], void>({
      query: () => client.getWorkItems(),
    }),
    getWorkItemsForms: builder.query<OrdersFormAnonymousViewModel[], void>({
      query: () => client.getWorkItemsForms(),
    }),
    getSupplierUpload: builder.query<SupplierUploadViewModel, string>({
      query: (supplierUploadId) => client.getSupplierUpload(supplierUploadId),
      providesTags: ['SupplierUploads'],
    }),
    getSupplierUploads: builder.query<SupplierUploadViewModel[], void>({
      query: () => client.getSupplierUploads(),
      providesTags: ['SupplierUploads'],
    }),
    postSupplierUpload: builder.mutation<FileResponse, SupplierUploadInputModel>({
      query: (model) => client.postSupplierUpload(model),
      invalidatesTags: [
        'SupplierUploads',
        'OrdersInternal',
        'OrdersSuppliers',
        'OrdersInternalAudit',
        'OrdersSuppliersAudit',
      ],
    }),
    getWorkflowConfigs: builder.query<WorkflowConfigViewModel[], void>({
      query: () => client.getWorkflowConfigs(),
      providesTags: ['WorkflowConfigs'],
    }),
  }),
  tagTypes: [
    'OrdersInternal',
    'OrdersSuppliers',
    'OrdersInternalAudit',
    'OrdersSuppliersAudit',
    'SupplierUploads',
    'WorkflowConfigs'
  ],
})

export function isSupplierOrder(
  order: OrderInternalViewModel | OrderSuppliersViewModel
): order is OrderSuppliersViewModel {
  return 'tenantId' in order && !!order.tenantId
}
export function isInternalOrder(
  order: OrderInternalViewModel | OrderSuppliersViewModel
): order is OrderInternalViewModel {
  return !('tenantId' in order) || !order.tenantId
}

export const {
  useGetOrdersSuppliersQuery,
  useGetOrderSuppliersQuery,
  useLazyGetOrderSuppliersDocumentQuery,
  useLazyGetOrdersSuppliersQuery,
  useUpdateOrderSuppliersMutation,
  useGetOrdersInternalQuery,
  useGetOrderInternalQuery,
  useLazyGetOrdersInternalQuery,
  useUpdateOrderInternalMutation,
  useLazyGetOrderInternalQuery,
  useLazyGetOrderInternalDocumentQuery,
  useLazyGetOrderSuppliersQuery,
  useGetOrderSuppliersAuditQuery,
  useGetOrderInternalAuditQuery,
  useGetOrderQuery,
  useGetOrdersFormQuery,
  useGetOrdersFormsQuery,
  useGetWorkItemsQuery,
  useLazyGetWorkItemsFormsQuery,
  useGetWorkItemsFormsQuery,
  useGetSupplierUploadQuery,
  useLazyGetSupplierUploadQuery,
  useGetSupplierUploadsQuery,
  usePostSupplierUploadMutation,
  usePostWorkItemMutation,
  useGetWorkflowConfigsQuery
} = ordersApi
